/**
 * CardsView — tryb „karty" dla ConfigurableTable.
 *
 * Renderuje rekordy jako siatkę kafelków zamiast wierszy tabeli. Tytuł kafelka
 * bierze kolumna wskazana przez `titleColumnId` (domyślnie pierwsza widoczna);
 * pozostałe kolumny pokazywane są jako pary etykieta: wartość. Wykorzystuje te
 * same `ColumnDefinition`
 * (renderCell, customLabel) co widok tabeli — żeden dodatkowy kontrakt danych.
 *
 * Przełącznik trybu siedzi w pasku `ConfigurableTable`; ten komponent dostaje
 * już przetworzone (posortowane/przefiltrowane) dane.
 */

import type React from 'react';
import type { ColumnConfig } from '@saas/shared';

import type { ColumnDefinition, RowActionsContext } from './types';

/** Kontekst akcji w stopce karty — stały obiekt, żeby `renderActions` nie dostawał nowego co render. */
const CARDS_ACTIONS_CONTEXT: RowActionsContext = { placement: 'cards' };

interface CardsViewProps<T> {
  /** Widoczne kolumny w kolejności (pierwsza = tytuł kafelka, o ile brak `titleColumnId`). */
  columns: ColumnDefinition<T>[];
  /** Id kolumny-tytułu kafelka; brak → pierwsza kolumna (zachowanie wsteczne). */
  titleColumnId?: string;
  /** Dane do wyświetlenia (już przetworzone przez ConfigurableTable). */
  data: T[];
  /** Identyfikator wiersza. */
  getRowId: (row: T) => string;
  /** Pobranie wartości komórki dla danej kolumny. */
  getCellValue: (row: T, column: ColumnDefinition<T>) => unknown;
  /** Nadpisana etykieta kolumny (customLabel). */
  getColumnConfig: (columnId: string) => ColumnConfig | undefined;
  /** Klik w kafelek. */
  onRowClick?: (row: T) => void;
  /** Akcje rekordu (renderowane w stopce kafelka); drugi argument = `{ placement: 'cards' }`. */
  renderActions?: (row: T, context: RowActionsContext) => React.ReactNode;
  /** Zaznaczanie kafelków. */
  selectable?: boolean;
  selectedIds?: string[];
  onRowSelect?: (rowId: string, checked: boolean) => void;
  /** Prefiks data-testid. */
  testIdPrefix?: string;
}

export function CardsView<T>({
  columns,
  titleColumnId,
  data,
  getRowId,
  getCellValue,
  getColumnConfig,
  onRowClick,
  renderActions,
  selectable = false,
  selectedIds = [],
  onRowSelect,
  testIdPrefix,
}: CardsViewProps<T>) {
  // Tytuł kafelka: kolumna wskazana przez `titleColumnId` (fallback pierwsza);
  // pozostałe widoczne kolumny lądują w treści karty (z zachowaniem kolejności).
  const titleColumn = titleColumnId
    ? (columns.find((c) => c.id === titleColumnId) ?? columns[0])
    : columns[0];
  const detailColumns = columns.filter((c) => c !== titleColumn);

  return (
    <div
      className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      data-testid={testIdPrefix ? `${testIdPrefix}-cards` : undefined}
    >
      {data.map((row, rowIndex) => {
        const rowId = getRowId(row);
        const isSelected = selectedIds.includes(rowId);
        const titleValue = titleColumn ? getCellValue(row, titleColumn) : null;
        const titleNode = titleColumn?.renderCell
          ? titleColumn.renderCell(titleValue, row, rowIndex)
          : String(titleValue ?? '—');

        return (
          <div
            key={rowId}
            data-testid={testIdPrefix ? `${testIdPrefix}-card-${rowId}` : undefined}
            onClick={() => onRowClick?.(row)}
            className={`flex flex-col rounded-lg border bg-bg-elevated p-4 transition-shadow ${
              onRowClick ? 'cursor-pointer hover:shadow-sm' : ''
            } ${isSelected ? 'border-accent-400 ring-1 ring-accent-400' : 'border-border-secondary'}`}
          >
            {/* Header: tytuł (np. email) + akcje (kebab „…") po prawej — wg Figmy „karty". */}
            <div className="mb-3 flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1 truncate text-sm font-semibold text-text-primary">
                {titleNode}
              </div>
              {(selectable || renderActions) && (
                <div
                  className="flex shrink-0 items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {selectable && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => onRowSelect?.(rowId, e.target.checked)}
                      className="mt-0.5 shrink-0 rounded border-border-primary text-accent-600 focus:ring-accent-500"
                      data-testid={
                        testIdPrefix ? `${testIdPrefix}-card-${rowId}-select` : undefined
                      }
                    />
                  )}
                  {renderActions?.(row, CARDS_ACTIONS_CONTEXT)}
                </div>
              )}
            </div>

            {/* Pola — etykieta w wyrównanej kolumnie + wartość (wg Figmy). */}
            <dl className="space-y-2 text-xs">
              {detailColumns.map((column) => {
                const value = getCellValue(row, column);
                if (value == null || value === '') return null;
                const config = getColumnConfig(column.id);
                const label = config?.customLabel || column.header;
                const rendered = column.renderCell
                  ? column.renderCell(value, row, rowIndex)
                  : String(value);
                return (
                  <div key={column.id} className="flex gap-2">
                    <dt className="w-28 shrink-0 text-text-muted">{label}:</dt>
                    {/* Wartość wyrównana do lewej. Renderery kolumn wyśrodkowane w tabeli
                        (np. checkbox „tak/nie" = `flex justify-center`) w karcie mają się
                        trzymać lewej — nadpisujemy tylko bezpośrednie dzieci-flex. */}
                    <dd className="min-w-0 flex-1 text-text-primary [&>.flex]:justify-start">
                      {rendered as React.ReactNode}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        );
      })}
    </div>
  );
}
